import { isPinnablePlacement as isPinnableTimelinePlacement } from '@src/editor/lib/placementEvaluation';
import { CompositionElement } from '@src/editor/models/elements/CompositionElement';
import { deepClone } from '@src/utility/deepClone';

import type {
  AITextBaseTextScope,
  CompositionTemplate,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { Track } from '@src/editor/models/Track';

type ExtraElementDataRoot = Record<string, unknown>;

export type VideoLike = { extraElementData?: unknown } | null | undefined;

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function ensureRoot(video: VideoLike): ExtraElementDataRoot | null {
  if (!video) return null;
  if (!isRecord(video.extraElementData)) {
    // initialize empty root
    Object.defineProperty(video, 'extraElementData', {
      value: {},
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  const rootUnknown = video.extraElementData;
  return isRecord(rootUnknown) ? rootUnknown : null;
}

/** Generic per-element object upsert (shallow merge). Creates object if missing. */
export function upsertElementMetaObject<T extends object>(
  video: VideoLike,
  elementId: string,
  patch: Partial<T>,
): void {
  const root = ensureRoot(video);
  if (!root) return;
  const existing = root[elementId];
  if (!isRecord(existing)) {
    root[elementId] = { ...patch };
    return;
  }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    existing[k] = v;
  }
}

/**
 * Remove specific keys from an element's meta object. No-op if the element
 * has no meta or the keys don't exist.
 */
export function deleteElementMetaKeys(
  video: VideoLike,
  elementId: string,
  keys: string[],
): void {
  const root = ensureRoot(video);
  if (!root) return;
  const existing = root[elementId];
  if (!isRecord(existing)) return;
  for (const k of keys) {
    delete existing[k];
  }
}

export function deleteElementMeta(video: VideoLike, elementId: string): void {
  const root = ensureRoot(video);
  if (!root) return;
  if (elementId in root) delete root[elementId];
}

/** Retrieve a deep copy of an element's meta object (if present). */
export function getElementMetaObject(
  video: VideoLike,
  elementId: string,
): Record<string, unknown> | undefined {
  const root = ensureRoot(video);
  if (!root) return undefined;
  const existing = root[elementId];
  if (!isRecord(existing)) return undefined;
  // Return a deep copy so callers can freely mutate without affecting source
  const cloned = deepClone(existing);
  if (isRecord(cloned)) return cloned;
  return undefined;
}

/**
 * PLAT-10923 — the composition template an element was added from, read without
 * the deep clone `getElementMetaObject` makes.
 *
 * Written at insert alongside the rest of an element's meta. Analytics reads it
 * on every event, and a graphic's meta carries its whole `compositionControls`
 * JSON — cloning that per event would be a real cost for one string.
 */
export function getElementTemplateId(
  video: VideoLike,
  elementId: string,
): string | undefined {
  if (!video || !isRecord(video.extraElementData)) return undefined;
  const entry = video.extraElementData[elementId];
  if (!isRecord(entry)) return undefined;
  return typeof entry.templateId === 'string' ? entry.templateId : undefined;
}

/**
 * PLAT-11816 — the facts the timeline block preview needs, read without
 * the deep clone `getElementMetaObject` makes (the block re-renders on every
 * timeline change and a graphic's meta carries its whole controls JSON).
 */
export interface ElementPreviewMeta {
  compositionType?: string;
  placement?: string;
  templateLabel?: string;
  templateThumbnailUrl?: string;
}

export function getElementPreviewMeta(
  video: VideoLike,
  elementId: string,
): ElementPreviewMeta | undefined {
  if (!video || !isRecord(video.extraElementData)) return undefined;
  const entry = video.extraElementData[elementId];
  if (!isRecord(entry) || entry.type !== 'composition') return undefined;
  const pick = (key: string): string | undefined => {
    const value = entry[key];
    return typeof value === 'string' ? value : undefined;
  };
  return {
    compositionType: pick('compositionType'),
    placement: pick('placement'),
    templateLabel: pick('templateLabel'),
    templateThumbnailUrl: pick('templateThumbnailUrl'),
  };
}

/**
 * PLAT-10915 — what an AI text generation left behind, per control (E5).
 *
 * Keyed by the control's `id` under `extraElementData[elementId].aiTextControls`
 * so several AI controls on one graphic each keep their own record.
 * `VideoRepository` serialises `extraElementData` to `extra_elements_json` on
 * save and rehydrates it on load, so this survives a story reload for free.
 */
export interface AITextProvenance {
  /** Which scope the generation read. */
  baseTextScope: AITextBaseTextScope;
  /**
   * Hash of the normalized base text at generation time
   * (`fingerprintAITextBaseText`). Written here, compared by PLAT-10918 — we
   * store the hash rather than the text because a clip transcript is easily
   * tens of thousands of characters and this blob rides on the video record.
   */
  sourceFingerprint: string;
  /** The creator's steer, when they gave one (PLAT-10917). */
  instructions?: string;
  /** ISO timestamp. */
  generatedAt: string;
  /** The preferred option first, then the alternatives (E9). */
  options: string[];
  /** Index into `options` currently written to the element. */
  selectedOption: number;
}

/**
 * Write one control's generation record, leaving the element's other meta —
 * and its other AI controls — untouched.
 */
export function upsertAiTextProvenance(
  video: VideoLike,
  elementId: string,
  controlId: string,
  provenance: AITextProvenance,
): void {
  if (!elementId || !controlId) return;
  const existing = readAiTextControls(video, elementId);
  upsertElementMetaObject(video, elementId, {
    aiTextControls: { ...existing, [controlId]: provenance },
  });
}

/**
 * Read one control's generation record, if it has a usable one.
 *
 * The blob is persisted JSON, so its shape is a runtime question, not a
 * compile-time one: a record written by an earlier shape would satisfy the cast
 * and then read as `sourceFingerprint: undefined`, which PLAT-10918 would
 * compare against a real fingerprint and call "Transcript changed" forever.
 * The two fields consumers actually branch on are checked here instead.
 */
export function getAiTextProvenance(
  video: VideoLike,
  elementId: string,
  controlId: string,
): AITextProvenance | undefined {
  const entry = readAiTextControls(video, elementId)[controlId];
  if (!isRecord(entry)) return undefined;
  if (typeof entry.sourceFingerprint !== 'string') return undefined;
  if (!Array.isArray(entry.options)) return undefined;
  return entry as unknown as AITextProvenance;
}

function readAiTextControls(
  video: VideoLike,
  elementId: string,
): Record<string, unknown> {
  // Read-only: deliberately NOT `ensureRoot`, which initializes the bag as a
  // side effect. PLAT-10916 calls `getAiTextProvenance` during render (the
  // picker reads the blob live), and a read that mutates the video model on
  // the way past is the kind of thing that is fine until the day it isn't.
  const root =
    video && isRecord(video.extraElementData) ? video.extraElementData : null;
  const meta = root?.[elementId];
  if (!isRecord(meta)) return {};
  const controls = meta.aiTextControls;
  return isRecord(controls) ? controls : {};
}

/**
 * True when the element meta describes an end card pinned to the timeline end
 * (`compositionType: 'endCard'`, `placement: 'end'`). Centralizes the "what is
 * an end card" rule so the editor (`PlacementReconciler`), the timeline store,
 * and the settings UI all agree on it.
 */
export function isEndCardMeta(meta: unknown): boolean {
  return (
    isRecord(meta) &&
    meta.compositionType === 'endCard' &&
    meta.placement === 'end'
  );
}

/**
 * PLAT-10921 — the timeline-placement facts the reconciler and the edit sites
 * both read off an element's meta, normalized into one shape.
 *
 * Everything here is about the TIMELINE (`time` / `duration`). `placement` is
 * overloaded in this codebase — `CompositionControls` uses the same word for
 * canvas `x/y/width/height` (DS2) — so read every field below as timeline-only.
 */
export interface PlacementMeta {
  /** The authored placement, e.g. `'clip'` / `'selection'` / `'start'` / `'end'`. */
  placement: string | null;
  /** `'endCard'` reads `placement: 'end'` as its START edge (DS1). */
  compositionType: string | null;
  /**
   * `placementPinOverride ?? placementPinned ?? false`.
   *
   * Absence means NOT pinned. `placementPinned` is written explicitly at
   * creation and by the E11 load-boundary normalization, never inferred from a
   * missing key — a legacy record has no flag *and* no enforcement, and
   * defaulting those to pinned would hand them to the reconciler and silently
   * move graphics in stories that are already published (E11).
   */
  pinned: boolean;
  /** The creator has manually changed this composition's timeline START. */
  startTimeOverride: boolean;
  /** The creator has manually changed this composition's timeline END. */
  endTimeOverride: boolean;
  /** Seconds an end card starts before the content end so it can transition in. */
  overlapBefore: number;
}

/**
 * Number-only, deliberately: this is byte-for-byte the old
 * `EndCardManager.getOverlapBefore` reading. Tolerating numeric STRINGS here
 * would be a behaviour change on the highest-risk path in the ticket — a
 * malformed `overlapBefore: "4"` was previously ignored (0) and would suddenly
 * start moving end cards in already-published stories.
 */
function readOverlapSeconds(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, value)
    : 0;
}

/** Reads the timeline-placement fields off an element's meta. */
export function readPlacementMeta(meta: unknown): PlacementMeta | null {
  if (!isRecord(meta)) return null;
  const pinOverride = meta.placementPinOverride;
  return {
    placement: typeof meta.placement === 'string' ? meta.placement : null,
    compositionType:
      typeof meta.compositionType === 'string' ? meta.compositionType : null,
    // PLAT-10922 supplies the creator-facing pin toggle that writes
    // `placementPinOverride`; the E11 normalization already writes it for
    // legacy hand-placed end cards, so it is read from day one rather than
    // left as data nothing consults.
    pinned:
      typeof pinOverride === 'boolean'
        ? pinOverride
        : meta.placementPinned === true,
    startTimeOverride: meta.placementStartTimeOverride === true,
    endTimeOverride: meta.placementEndTimeOverride === true,
    overlapBefore: readOverlapSeconds(meta.overlapBefore),
  };
}

/**
 * The overlaps a composition was placed with, as the ripple config that placed
 * it. Adding a card ripples by `duration - overlapBefore - overlapAfter`, so
 * removing it has to reverse the same amount or the timeline drifts by the
 * overlap — which is what let an intro-card toggle collapse a transcript cut.
 *
 * Same number-only reading as `readPlacementMeta`, so a malformed
 * `overlapAfter: "3"` keeps being ignored rather than starting to move
 * elements in already-published stories.
 */
export function readRippleOverlaps(meta: unknown): {
  overlapBefore: number;
  overlapAfter: number;
} {
  if (!isRecord(meta)) return { overlapBefore: 0, overlapAfter: 0 };
  return {
    overlapBefore: readOverlapSeconds(meta.overlapBefore),
    overlapAfter: readOverlapSeconds(meta.overlapAfter),
  };
}

/** True when the element meta describes the timeline's intro card. */
export function isIntroCardMeta(meta: unknown): boolean {
  return (
    isRecord(meta) &&
    (meta.compositionType === 'introCard' || meta.placement === 'start')
  );
}

/**
 * Finds the end time of the intro card on the timeline, if one exists.
 * Accounts for overlap so the returned time reflects the non-overlapping portion.
 *
 * NOTE the sibling below. This one subtracts `overlapAfter` — the moment the
 * intro *begins handing over* — which is what its transcript-restore callers
 * need and is NOT the scope bound a pinned graphic starts at.
 */
export function findIntroCardEndTime(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
): number {
  for (const track of tracks) {
    for (const element of track.elements) {
      if (!(element instanceof CompositionElement)) continue;

      const meta = extraData[element.id];
      if (isIntroCardMeta(meta) && isRecord(meta)) {
        let overlapAfter = 0;
        if ('overlapAfter' in meta && meta.overlapAfter) {
          overlapAfter = parseFloat(String(meta.overlapAfter));
        }
        return element.end_time - overlapAfter;
      }
    }
  }

  return 0;
}

/**
 * PLAT-10921 — the intro card's FULL end time, overlap included.
 *
 * "You start when the start card stops": a graphic pinned into the clip must
 * clear the card entirely, so this deliberately does **not** subtract
 * `overlapAfter` the way `findIntroCardEndTime` (directly above) does. A
 * sibling rather than a flag on that function: changing it in place would alter
 * transcript restore, its only two callers.
 *
 * Returns `null` when the timeline has no intro card, so callers can tell
 * "no card" from "a card ending at 0".
 *
 * The LATEST intro end, not the first one found — "you start when the start
 * card stops" means clearing every start card, so with two of them the later
 * one is the bound. Mirrors `findEndCardScopeStart`, which takes the earliest
 * end card for the same reason at the other end.
 */
export function findIntroCardScopeEnd(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
): number | null {
  let latest: number | null = null;

  for (const track of tracks) {
    for (const element of track.elements) {
      if (!(element instanceof CompositionElement)) continue;
      if (!isIntroCardMeta(extraData[element.id])) continue;
      if (latest === null || element.end_time > latest) {
        latest = element.end_time;
      }
    }
  }

  return latest;
}

/**
 * Stores composition template metadata for an element.
 * This associates a composition template with an element and stores its configuration.
 */
export function storeCompositionTemplateMetadata(
  video: VideoLike,
  elementId: string,
  template: CompositionTemplate,
): void {
  upsertElementMetaObject(video, elementId, {
    type: 'composition',
    templateId: template.id,
    templateLabel: template.label,
    templateThumbnailUrl: template.thumbnail?.url,
    compositionControls: template.controls,
    isDesignComposition: true,
    compositionType: template.compositionType,
    overlapBefore: template.overlapBefore,
    overlapAfter: template.overlapAfter,
    placement: template.placement || null,
    // PLAT-10921 — written explicitly at creation, never inferred from absence.
    // Pinned exactly when the placement binds a timeline edge at all: `clip` /
    // `selection` / `start` / `end`. `current_time` and `section` describe only
    // where a graphic is inserted, so there is nothing for a pin to enforce —
    // which is what keeps every pre-existing `current_time` record inert.
    placementPinned: isPinnableTimelinePlacement(template.placement),
    isFullscreen: template.isFullscreen || false,
    isGlobalPreset: template.isGlobalPreset || false,
    sixteenByNine: template.sixteenByNine || null,
    nineBySixteen: template.nineBySixteen || null,
    oneByOne: template.oneByOne || null,
  });
}
